<template>
  <div
    v-if="isRwa"
    class="faq-page"
  >
    <div class="tabs-wrap">
      <USelectButton
        :options="tabs"
        :default-active="1"
        @change="activeTab = $event"
      />
    </div>

    <div
      v-if="activeTab === 'FAQ'"
      class="content-wrap"
    >
      <div
        v-for="(section, sIdx) in faqSections"
        :key="sIdx"
        class="faq-section"
      >
        <div class="section-title h2">
          {{ section.title }}
        </div>
        <AuAccordion
          v-for="(item, iIdx) in section.items"
          :key="iIdx"
        >
          <template #title>
            <span class="question">{{ item.question }}</span>
          </template>
          <template #content>
            <p class="answer">
              {{ item.answer }}
            </p>
          </template>
        </AuAccordion>
      </div>
    </div>

    <div
      v-if="activeTab === 'Glossary'"
      class="content-wrap"
    >
      <div class="table-wrap">
        <UTable
          :items="glossaryTerms"
          :columns="tableColumns"
        >
          <template #title="{ value }">
            {{ value }}
          </template>
          <template #description="{ value }">
            {{ value }}
          </template>
        </UTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import AuAccordion from "@/components/extended/AuAccordion.vue";
import USelectButton from "@/components/uikit/uComponents/USelectButton.vue";
import UTable from "@/components/uikit/uComponents/UTable.vue";
import { checkAccess, scope } from "@/help/accessData";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const isRwa = computed(() => !checkAccess(scope.notRwa));

onMounted(() => {
  if (checkAccess(scope.notRwa)) {
    router.push({ name: "page not found" });
  }
});

const tabs = ["FAQ", "Glossary"];
const activeTab = ref("FAQ");

const tableColumns = ref([
  {
    property: "title",
    header: "Term",
    align: "center"
  },
  {
    property: "description",
    header: "Definition",
    align: "start"
  }
]);

const glossaryTerms = ref([
  {
    title: "AML (Anti-Money Laundering)",
    description: "Anti-money laundering procedures. In the context of RWA.capital, this implies verification of the source of funds, investor identity, and the nature of transactions prior to onboarding as well as for any subsequent transactions."
  },
  {
    title: "Annual Administration Fee",
    description: "An annual administrative fee for maintaining the investment structure, including SPV maintenance, reporting, paperwork, distribution coordination, basic investor servicing, and operational administration."
  },
  {
    title: "Capital Growth",
    description: "The increase in the market value of a real estate asset over time. This is one of the two main components of total return, alongside rental income."
  },
  {
    title: "Client Money Account",
    description: "A segregated client money arrangement where investor funds are kept separate from the platform's operating funds. This segregation reduces the risk of commingling client funds with business expenses."
  },
  {
    title: "Commitment",
    description: "A confirmed intention by an investor to invest a certain amount in a particular property or funding round prior to final closing. At certain stages, the commitment may be withdrawn."
  },
  {
    title: "Crowdfunding",
    description: "A collective funding model following which multiple investors contribute relatively small amounts into a single asset. In real estate, this enables access to properties without purchasing the entire asset."
  },
  {
    title: "DFSA (Dubai Financial Services Authority)",
    description: "The financial regulator of the DIFC. The platform is registered and regulated subject to DFSA rules. DFSA requirements typically relate to compliance, client money, disclosures, and investor protection."
  },
  {
    title: "DIFC (Dubai International Financial Center)",
    description: "Dubai's financial jurisdiction with its own legal framework and corporate infrastructure."
  },
  {
    title: "Dividend Distribution",
    description: "The distribution to investors of their share of net rental income or other investment proceeds. Distributions are typically made after deduction of operating expenses and applicable fees."
  },
  {
    title: "DLD (Dubai Land Department)",
    description: "The Dubai government authority in charge of registering real estate ownership rights and managing related matters."
  },
  {
    title: "DLD Fee",
    description: "A government registration fee charged by the Dubai Land Department in connection with the purchase and registration of a property. It is typically treated as part of transaction costs rather than platform revenue."
  },
  {
    title: "Entry Fee / Acquisition Fee",
    description: "A fee charged upon entry into an investment. It typically covers deal structuring, offering arrangement, acquisition support, and the platform's operational work in onboarding the investor into the asset."
  },
  {
    title: "Exit Fee",
    description: "A fee charged upon the sale of an interest or full exit from an investment. It is typically calculated as a percentage of the sale price or exit proceeds."
  },
  {
    title: "Exit Strategy",
    description: "A predefined exit framework for investors, such as the sale of the entire property, sale of an interest during a liquidity window, transfer of the interest to another investor, or another specified mechanism."
  },
  {
    title: "Exit Window",
    description: "A period during which the platform allows investors to buy and sell interests under secondary trading rules. This is not guaranteed instant liquidity, but a time-limited exit mechanism."
  },
  {
    title: "Gross Yield",
    description: "Gross rental return before expenses are deducted: rent divided by the value of the asset or investment. This metric is useful for initial assessment yet will not reflect the net cash outcome."
  },
  {
    title: "Hold Strategy",
    description: "A long-term ownership strategy focused on generating recurring rental income and potential capital appreciation. Unlike a short-term flip, it is designed for a longer holding horizon."
  },
  {
    title: "KYC (Know Your Customer)",
    description: "A client identification procedure involving the collection of identity documents, address details, source of funds, as well as any other information required to allow investment in a regulated environment."
  },
  {
    title: "Liquidity",
    description: "An investor's ability to convert an interest into cash quickly without a material loss in value. In fractional real estate, liquidity is typically lower than in publicly traded securities."
  },
  {
    title: "Material Change",
    description: "A material change in circumstances that may affect the value of the asset, expected return, holding period, transaction structure, or the investment's risk profile."
  },
  {
    title: "Minimum Investment",
    description: "The minimum amount required to enter into an investment. For RWA.capital, the threshold is from AED 1,000 unless otherwise specified for a particular offering."
  },
  {
    title: "NAV / Investment Value",
    description: "The calculated value of an investor's interest or the current valuation of the investment. It may be based on property value, cash flows, independent valuation, and the platform's valuation methodology."
  },
  {
    title: "Net Rental Income",
    description: "Net rental income after deduction of applicable expenses, including management fees, service charges, repairs, insurance, vacancy costs, administrative costs, and other expenses."
  },
  {
    title: "POA (Proof of Address)",
    description: "A document serving to confirm the investor's address, such as a bank statement or utility bill. It is used in KYC/AML checks."
  },
  {
    title: "Portfolio Diversification",
    description: "Portfolio diversification achieved through allocating capital across multiple properties, strategies, areas, or property types rather than concentrating in a single investment."
  },
  {
    title: "Professional Investor",
    description: "An investor category that, subject to meeting platform or regulatory criteria, may benefit from higher limits, broader access, and a different level of disclosures compared to a retail investor."
  },
  {
    title: "Property Manager",
    description: "The party responsible for the operational management of the property, including tenants, rent collection, maintenance, contractors, vacancy management, and day-to-day operations."
  },
  {
    title: "Referral Program",
    description: "A referral scheme under which a user may receive a bonus or other reward for introducing a new client subject to the platform's rules."
  },
  {
    title: "Rental Income",
    description: "Income generated from leasing a property. For investors, this is one of the primary sources of cash flow in an income-producing real estate model."
  },
  {
    title: "Rental Yield",
    description: "Rental return expressed as a percentage. It may be calculated as gross yield or net yield, depending on whether expenses are taken into account."
  },
  {
    title: "Retail Investor",
    description: "A standard investor category that is generally subject to stricter investment limits, standard disclosure requirements, and a basic set of permitted transactions."
  },
  {
    title: "Secondary Market",
    description: "A mechanism for the resale of interests between investors without requiring the sale of the entire property."
  },
  {
    title: "Service Charges",
    description: "Recurring costs related to the maintenance of the property and building, including common areas, operations, infrastructure, utility-related components, and property management. These affect net returns."
  },
  {
    title: "SPV (Special Purpose Vehicle)",
    description: "A separate legal entity established for a specific real estate asset. In a crowdfunding model, the SPV holds legal title to the property, while investors hold shares or interests in that entity."
  },
  {
    title: "Title Deed",
    description: "The official legal document evidencing ownership of a real estate asset. In an SPV structure, the title deed usually names the SPV rather than all underlying investors."
  },
  {
    title: "Total Return",
    description: "The overall return on an investment, combining current rental income and the change in asset value over the holding period."
  },
  {
    title: "Units / Shares",
    description: "The investor's units of participation in the transaction structure."
  },
  {
    title: "Valuation",
    description: "An assessment of the market value of the property by an independent valuer or under an internal methodology. It is used for pricing, value disclosure, and sale decisions."
  },
  {
    title: "VAT (Value Added Tax)",
    description: "Value Added Tax, which may apply to certain services, fees, or components of transaction costs."
  },
  {
    title: "Wallet / eWallet",
    description: "The user's internal wallet on the platform through which top-ups, income distributions, fund reservations for investments, and withdrawals are processed."
  }
]);

const faqSections = ref([
  {
    title: "1. RWA.capital Platform: General Info",
    items: [
      {
        question: "What is RWA.capital?",
        answer: "RWA.capital is a digital investment platform for collective investment into selected real estate and other real-world asset opportunities. The platform enables users to complete onboarding online, fund their wallet, select an investment opportunity, invest, receive reports, and track performance through a single interface. The standard minimum investment threshold on the platform is AED 1,000, unless separate terms are published for a specific opportunity."
      },
      {
        question: "Why invest through RWA.capital rather than purchase a property independently?",
        answer: "The crowdfunding model lowers the entry threshold, offers access to diversification across multiple assets, and removes much of the operational burden from the investor. Instead of independently sourcing properties, handling legal documentation, managing tenants, and maintaining the asset, the investor gains access to a structured investment product, centralized servicing, and reporting. This is particularly convenient for investors seeking exposure to the UAE real estate market with a smaller ticket size and without day-to-day operational involvement."
      },
      {
        question: "How does the platform function in general?",
        answer: "First, the investor creates an account and completes KYC/AML verification. The investor then funds the wallet or selects an available opportunity, reviews the property page, risks, expenses, and target return, and confirms the investment. Once the funding round is closed, the asset is transferred into a dedicated holding structure, and the investor receives digital documentation, access to reporting, income distributions, and available exit mechanisms."
      },
      {
        question: "Is this a short-term or long-term investment?",
        answer: "The core real-estate platform investing model is generally designed for a medium- to long-term horizon: investors may earn returns from rental income, capital appreciation, or a combination of both. However, the platform may also offer strategies with a shorter investment cycle, such as flip transactions, value-add projects, or off-plan opportunities. The actual investment horizon is always specified on the page of the relevant opportunity."
      },
      {
        question: "What services does RWA.capital provide?",
        answer: "As a rule, the platform facilitates digital investor onboarding, KYC/AML checks, deal sourcing and structuring, legal execution support, receipt and administration of client funds, post-investment reporting, distribution processing, coordination with property managers, and, where available, secondary exit mechanisms. For certain products, additional services may include resale support, reinvestment options, and special investment programs."
      },
      {
        question: "Can I invest in case I do not live in the UAE or if I relocate?",
        answer: "In many cases, yes, provided the investor meets the platform's eligibility, identity verification, and source-of-funds requirements, and their country is not subject to internal restrictions or sanctions prohibitions. In the event you change your country of residence, tax status, phone number, bank details, or identification documents, these should be updated in your profile promptly. The current list of eligible countries and restrictions is published on the website."
      }
    ]
  },
  {
    title: "2. Account, Investor Eligibility, and Documentation",
    items: [
      {
        question: "Who can invest through RWA.capital?",
        answer: "Adults who successfully complete registration and KYC/AML verification may invest through the platform, provided their jurisdiction is permitted under the platform's rules. Certain investors may be subject to restrictions depending on their country of residence, client classification, the nature of the product, and applicable local legal requirements. For some opportunities, the terms may differ for retail and professional investors."
      },
      {
        question: "How do I open an account and start investing?",
        answer: "The process typically includes registration by email or phone number, creation of a password, confirmation of contact details, completion of the investor questionnaire, and upload of supporting documents. Once verification is successfully completed, the user may fund the wallet or reserve an amount directly in an available investment opportunity. Before investing, users are encouraged to review the investment term, target return, fee structure, exit limitations, and all disclosure documents carefully."
      },
      {
        question: "Do I have to pay to register on the platform?",
        answer: "As a general rule, registration and account creation are free of charge. Costs typically arise at the stage of a specific investment, in the form of platform fees, deal structuring expenses, transaction costs, or exit fees, where applicable under the terms of the product."
      },
      {
        question: "Why am I asked to provide personal data and documents?",
        answer: "This is standard practice for KYC/AML purposes, sanctions screening, source-of-funds verification, and compliance with anti-money laundering and counter-terrorist financing requirements. The platform must ensure that the investor has been properly identified and that payments comply with internal compliance policies and applicable regulations. Without such verification, access to investments, wallet funding, or withdrawals may be restricted."
      },
      {
        question: "What documents are usually required for verification?",
        answer: "A valid passport or Emirates ID, a selfie and/or biometric verification, proof of residential address, and, in some cases, additional evidence of income or source of funds are typically required. For corporate investors or larger investment amounts, corporate documents, ownership structure information, bank statements, and additional questionnaires may also be requested. The exact list depends on the client type and the level of regulatory review required."
      },
      {
        question: "What is Proof of Address?",
        answer: "Proof of Address is a document confirming the investor's actual residential address. This may typically include a utility bill, bank statement, official government document, or another document showing both the investor's name and address. As a rule, the document must be recent and must satisfy the platform's requirements as to issue date and legibility."
      },
      {
        question: "How does a retail investor differ from a professional investor?",
        answer: "A retail investor invests within the platform's standard limits and product restrictions, whereas a professional investor may gain access to more sophisticated opportunities, higher investment limits, or alternative structures. The criteria for professional investor status generally depend on capital levels, investment experience, regulatory assessments, or verified financial standing."
      },
      {
        question: "How much can I invest?",
        answer: "The minimum investment amount on RWA.capital is AED 1,000. The maximum is subject to the platform's rules, investor classification, the terms of the specific opportunity, the proportion that a single investor may hold in one asset, and applicable regulatory limits. Any individual restrictions are shown on the investment page and/or in the legal documents before the investment is confirmed."
      },
      {
        question: "How can I submit a complaint or escalate an issue?",
        answer: "We maintain a transparent complaints process, including a submission channel, response timelines, escalation procedures, and, where relevant, a reference to an external regulatory route. A complaint may be submitted through the help center or the official compliance team email address. To facilitate a prompt review, it is helpful to include the account number, deal number, transaction date, and a brief description of the issue."
      }
    ]
  },
  {
    title: "3. How Asset Ownership Is Structured",
    items: [
      {
        question: "What exactly does the investor invest in: the property itself or an interest in a holding structure?",
        answer: "The investor does not acquire a direct interest in the title deed in their own name, but rather an interest in a special holding structure that owns the asset. Economically, the investor receives a proportional entitlement to the income and value of the property through the number of shares, units, or ownership interests held. This model simplifies collective ownership, corporate governance, and the eventual exit process."
      },
      {
        question: "What is an SPV and why is it used?",
        answer: "An SPV (Special Purpose Vehicle) is a separate legal entity established specifically for a particular investment transaction or property. Through an SPV, a large number of investors can participate in a single asset, the property can be legally separated from the platform's operating business, and ownership documentation can be standardized. For the investor, this results in a more transparent structure, a clearer cap table, and a cleaner framework for rights and distributions."
      },
      {
        question: "How does the SPV protect investor interests?",
        answer: "The SPV functions as a ring-fencing mechanism: the asset and its related obligations are isolated within a separate structure and should not be commingled with assets from other transactions or with the platform's general business risks. If one investment encounters difficulties, this does not affect other SPVs. In addition, this structure facilitates audit, investor registry maintenance, and documentation of ownership rights."
      },
      {
        question: "In whose name is the title deed registered?",
        answer: "In a fractional ownership model, the title deed is generally registered in the name of the SPV rather than in the names of dozens or hundreds of individual investors. The investor holds shares or interests in that structure, while the structure itself holds the real estate. Where the transaction involves the direct purchase of a whole asset or a separate private/off-plan product, the ownership structure may differ and is described in the transaction documents."
      },
      {
        question: "How many shares, interests, or units will I receive?",
        answer: "The number of shares or units depends on the size of your investment relative to the total transaction value and the number of issued interests in the structure. The exact number will be disclosed to investors before the transaction is confirmed, together with the full fee and expense structure."
      },
      {
        question: "What happens if a deal does not raise the required amount or, conversely, is oversubscribed?",
        answer: "If the funding round does not reach the minimum required amount, we will generally either cancel the opportunity or extend the funding period; in the event of cancellation, all reserved funds will be returned to investors in accordance with the rules of the round. If demand exceeds the available allocation, partial allocation or allocation based on investment order may apply, and the round may also close earlier than originally indicated, as disclosed in the documentation for that investment."
      },
      {
        question: "What documents does the investor receive after the transaction?",
        answer: "Following closing, the investor will typically receive subscription documents, investment confirmation, an agreement or participation certificate, a statement confirming the number of shares/interests held, as well as ongoing updates relating to the SPV and the asset."
      },
      {
        question: "How can I verify my ownership rights and the authenticity of the documents?",
        answer: "Verification is generally carried out through the personal account, SPV corporate documentation, payment confirmations, and, where applicable subject to the transaction structure, through asset registration details in official registries. In certain cases, the platform provides guidance on how to verify the registration details of the holding company or the asset. If the investor has any remaining questions, they should request the full post-closing documentation package from support."
      }
    ]
  },
  {
    title: "4. Asset Selection and Investment Strategies",
    items: [
      {
        question: "How does RWA.capital select assets?",
        answer: "The platform generally reviews the location, property type, rental demand history, return potential, liquidity, quality of the developer or seller, legal cleanliness, and the expected exit scenario. Due diligence, assessment of expected expenses, verification of the tenant or operating model, and consistency with the relevant investment strategy all play an important role. As a rule, only a portion of the reviewed opportunities that pass the internal selection process are listed on the platform."
      },
      {
        question: "What investment strategies may be available on the platform?",
        answer: "In most cases, opportunities fall into hold strategies focused on rental income, capital appreciation, or a combination of both. The strategy for each investment is clearly stated on the product page together with its main return drivers."
      },
      {
        question: "Can I choose the asset I want to invest in?",
        answer: "Yes, the investor independently selects an opportunity from the available catalogue and makes a decision based on the materials provided on the platform. If you choose the automated investing function, you set your own criteria and limits, but manual investing usually remains available as a separate option. The final investment decision always remains with the investor."
      },
      {
        question: "Who manages the property after acquisition? Can I visit the property?",
        answer: "Operational management is generally handled by an appointed servicing partner. This party is responsible for tenants, maintenance, rent collection, basic reporting, and implementation of the property budget. The possibility of a physical visit depends on the status of the asset, existing tenants, building security requirements, and the internal policies of the managing party."
      },
      {
        question: "Are the investment properties insured and who is responsible for maintenance?",
        answer: "As a general rule, the property is insured to the extent appropriate for its type, tenancy status, and the policy of the managing party. Ongoing costs related to maintenance, repairs, insurance, common-area utility charges, and management services are paid at the property or SPV level and are reflected in the transaction's financial model. The full list of operating expenses is disclosed to investors before closing and in regular reporting."
      },
      {
        question: "Can investors be asked to contribute additional capital following the acquisition?",
        answer: "In standard retail products, this is generally minimized and anticipated in the expense reserve wherever possible. However, in exceptional circumstances, such as major unplanned repairs, litigation, vacancy levels above expectations, or another material change, the platform may ask investors to approve additional measures. The procedure applicable in such cases is described in the transaction documents and the SPV's corporate rules."
      },
      {
        question: "What happens if there is a material change in circumstances affecting the property?",
        answer: "If a material change occurs, for example a significant decline in rental income, unexpected repair cost increases, a regulatory change, a title dispute, or another event affecting value or returns, we will seek to notify investors and explain the available courses of action. This may include an updated forecast, a revised budget, an investor vote, a sale of the asset, or other corrective measures."
      }
    ]
  },
  {
    title: "5. Wallet Funding and the Investment Process",
    items: [
      {
        question: "What funding methods and payment options are available for investments?",
        answer: "Available methods typically include bank transfers, local payment methods, and, in some cases, cards and instant bank transfers. Before funding the wallet, it is important to ensure that the payment is made from an account held in the investor's own name, as required under the platform's AML policy."
      },
      {
        question: "How quickly will funds appear in my wallet?",
        answer: "The time required for funds to be credited depends on the payment method used: instant banking and local transfers may appear more quickly, while international transfers generally take longer. Timing may also be affected by the sending bank, business days, verification of the account holder's name, and internal compliance checks. If a transfer is delayed, the investor will usually be asked to provide proof of payment through support."
      },
      {
        question: "Can I connect a bank account for instant transfers, and do I need to do this every time?",
        answer: "If instant bank transfer is available on the platform, the investor will generally complete a secure bank account linking process through a third-party payment provider. Depending on the provider, repeated linking may not be required for each new payment if the bank account and device have already been verified. The ability to link multiple accounts and the list of supported banks depend on the relevant payment integration."
      },
      {
        question: "Are funds in the wallet protected, and is interest paid on the balance?",
        answer: "Client funds are generally held separately from the platform's operating accounts in accordance with applicable client money handling rules. This reduces the risk of commingling company funds with investor funds, but it does not constitute a full bank guarantee or insurance against all risks."
      },
      {
        question: "Can I invest in several properties at the same time?",
        answer: "Yes. One of the platform's core principles is to enable investors to allocate capital across multiple opportunities, segments, or strategies. This helps diversify risk across locations, tenants, investment horizons, and asset types. Each investment, however, is treated as a separate transaction with its own terms, reporting, and potential exit restrictions."
      },
      {
        question: "Can I cancel an application or withdraw my commitment before closing?",
        answer: "Until a transaction has been finally closed and funds have been allocated, the platform allows cancellation within the applicable cancellation window (48 hours). Once the transaction has closed or the subscription for interests has been completed, the right to cancel unilaterally will generally no longer be available, except where expressly provided in the transaction documents. Investors are therefore strongly encouraged to check the cut-off time and cancellation rules before confirming an investment."
      },
      {
        question: "What is the minimum threshold for auto-investing or repeat investments?",
        answer: "The standard minimum investment threshold on the platform is AED 1,000, and this should generally be used as the reference point for repeat investments as well, unless a different minimum is specified for a particular opportunity. Investors should always rely on the amount shown in the interface immediately before confirming the transaction."
      }
    ]
  },
  {
    title: "6. Returns, Distributions, and Performance Measurement",
    items: [
      {
        question: "How does an investor earn returns on RWA.capital?",
        answer: "Investor returns are typically generated from two primary sources: recurring rental cash flow and capital appreciation realized upon sale of the asset. Some strategies are focused mainly on current income, while others are more heavily weighted toward capital appreciation; in value-add or flip transactions, profit may be realized primarily on exit. All distributions are made pro rata to the investor's holding after deduction of applicable expenses and fees."
      },
      {
        question: "What level of return can be expected?",
        answer: "The platform may publish projected returns, target rental yield, IRR, or expected total return, yet such figures are forecasts only and do not constitute guarantees. Actual performance depends on rental income, vacancy, timing of sale, financing costs, maintenance expenses, regulatory factors, and market conditions. Investors should distinguish carefully between target return, net return, and cash-on-cash distributions."
      },
      {
        question: "When and how are returns distributed?",
        answer: "The distribution schedule depends on the product. In hold strategies, rental income distributions are generally made periodically, for example monthly or at another stated frequency, while capital appreciation is realized upon sale of the asset. In flip transactions, returns may be distributed at the end of the project once the works have been completed and the asset has been sold."
      },
      {
        question: "How can I understand the current value of my investment?",
        answer: "The current valuation may be based on the latest property valuation and the current financial model. Upon request, the platform may display an indicative fair value, but this may not necessarily correspond to the price that could be achieved in an actual exit at that specific time."
      },
      {
        question: "What is the difference between hold and flip investments?",
        answer: "Hold transactions are designed for longer-term ownership of an asset with rental income during the holding period and a sale at a later stage. Flip transactions, by contrast, involve a shorter value-creation cycle based on the acquisition, improvement, repositioning, and sale of the asset, often without regular interim distributions. Flip products typically involve higher execution risk, greater sensitivity to exit timing, and may have a separate minimum ticket size for the relevant transaction."
      }
    ]
  },
  {
    title: "7. Fees, Costs, and Taxes",
    items: [
      {
        question: "What fees may RWA.capital charge?",
        answer: "The fee model may include some or all of the following elements: a one-time entry fee, an annual administration or servicing fee, a potential performance fee on profits, and an exit or secondary transfer fee. Certain products may also involve separate subscription, success, brokerage, or structuring fees. All fees should be disclosed on the investment page on a net basis so that the investor can understand the overall economics of the transaction."
      },
      {
        question: "What additional transaction-related costs typically exist apart from the platform fee?",
        answer: "In addition to the platform fee, the investor may indirectly bear a share of transaction-related and operational costs at the asset level, including registration fees, transfer fees, trustee fees, broker fees, SPV setup and maintenance costs, insurance, legal support, technical inspections, maintenance, repairs, and other third-party costs."
      },
      {
        question: "Are there any fees for funding, withdrawing, or transferring money?",
        answer: "The platform itself does not charge a fee for standard wallet funding or withdrawals, but the investor may incur charges from their bank, payment provider, or currency conversion. Terms may differ for instant banking and international transfers. Investors should therefore always review any external banking charges applicable to the transaction."
      },
      {
        question: "What fees apply when selling interests or using the secondary market?",
        answer: "Where an exit is made through the secondary market or a share transfer facility, a seller fee, a buyer fee, or both may apply, together with technical costs associated with processing the transfer."
      },
      {
        question: "What is cost reimbursement?",
        answer: "Cost reimbursement is a mechanism under which expenses previously incurred by one of the parties to the transaction, for example structuring expenses, registration fees, due diligence costs, or other outlays initially paid by the seller, the SPV, or the platform, are subsequently reimbursed and allocated among the participants in the transaction."
      }
    ]
  },
  {
    title: "8. Exit and Liquidity",
    items: [
      {
        question: "How can an investor exit an investment?",
        answer: "Several exit scenarios may be available, including a sale of the entire asset at the end of the investment period, an earlier sale of interests through a secondary mechanism, or a buyout/liquidation under the special rules of a particular product. Not every transaction is freely liquid, and the existence of an exit mechanism does not guarantee that an interest can be sold at any time."
      },
      {
        question: "What is the secondary market or share transfer facility?",
        answer: "This is a platform-based mechanism through which one investor may offer their interests for sale to another investor. While such a tool may improve liquidity, it does not convert the interests into a fully exchange-traded asset: transfers are generally carried out under defined rules, within limited periods, and only after the purchaser has passed the relevant checks."
      },
      {
        question: "Why is the secondary market or sale window open only for a limited period?",
        answer: "Limited windows allow the platform to synchronize compliance checks, documentation flow, record dates, cap table updates, and SPV corporate records. This also reduces operational complexity and enables all transactions to be processed in an orderly manner."
      },
      {
        question: "Can I sell only part of my holding? Is there a minimum sale amount?",
        answer: "The platform may establish a minimum number of shares or units that may be sold, or a minimum residual holding value that must remain after the sale."
      },
      {
        question: "Who sets the price on the secondary market, and are there any restrictions?",
        answer: "The price may be set by the seller within a defined pricing corridor, based on or after the latest valuation/NAV. Restrictions are applied to reduce the risk of price manipulation, non-arm's-length transactions between related parties, and investor error when setting the price."
      },
      {
        question: "How long does it take to transfer purchased interests to a new investor?",
        answer: "The timing depends on completion of payment, purchaser verification, transfer documentation, internal cut-off times, and updates to the register of ownership interests. In practice, a transfer is rarely instantaneous: the platform first confirms payment and compliance, then updates the ownership records, and only afterwards reflects the new holding in the user account. Until the transfer is complete, the investor should not treat the transaction as finally settled."
      },
      {
        question: "What happens at the end of the investment term?",
        answer: "At the end of the holding period, the asset is generally sold, or investors may be asked to vote on an extension, refinancing, an alternative exit strategy, or continued ownership. Following final realization, the remaining capital and profit are distributed after deduction of expenses and fees."
      },
      {
        question: "Can I cancel an investment after subscribing for interests?",
        answer: "Once the subscription has closed, funds have been allocated, and the investor has formally been included in the ownership structure, the right to cancel unilaterally is limited. Exceptions may apply only in cases expressly listed in the transaction documents or where the placement process has not been properly followed."
      }
    ]
  },
  {
    title: "9. Account Security and Operational Controls",
    items: [
      {
        question: "Is it mandatory to enable MFA, and what is a security PIN?",
        answer: "Multi-factor authentication (MFA) is one of the key layers of account protection. A security PIN may be used as an additional local confirmation layer for transactions within the mobile application. Together, these measures significantly reduce the risk of unauthorized access to the account."
      },
      {
        question: "What should I do if I lose access to my authenticator app or want to reset my PIN?",
        answer: "In that case, the investor will generally need to complete a recovery procedure through support: confirming identity, repeating part of the KYC process, and verifying ownership of the account, after which MFA can be reconnected or the PIN can be reset."
      },
      {
        question: "Can the platform employ an AI assistant, and can I rely on it as investment advice?",
        answer: "An AI assistant should be treated primarily as an educational or navigational tool: it may explain terms, indicate where documents are located, and help users navigate the interface. However, such tools should not replace the official transaction terms, disclosure documents, or the investor's own assessment of risk. The investment decision remains the investor's responsibility, and AI-generated responses should always be checked against the platform's primary documentation."
      },
      {
        question: "What happens to investments in case the investor passes away?",
        answer: "The investor's rights will generally pass to their heirs in accordance with applicable law and the platform's internal procedures. The heirs or their representatives must provide legal documentation confirming their entitlement, after which the platform will update the ownership register or arrange the appropriate next steps for the account. In practice, this process may take time and may require separate legal review."
      }
    ]
  },
  {
    title: "10. Regulation, Risks, and Investor Protection",
    items: [
      {
        question: "Is RWA.capital regulated?",
        answer: "Yes. RWA.capital is regulated by the DFSA. This information is available in the legal section, the terms of use, and the offering documents on the website. Before investing, users are also encouraged to verify the company's official registration details on the DIFC and DFSA websites."
      },
      {
        question: "What happens to my investments if the platform ceases operations or becomes insolvent?",
        answer: "The key investor protections are based on the segregation of client funds and the fact that the asset is generally held through a separate SPV or similar holding structure, rather than by the platform's operating company itself. This does not eliminate all risks, but it reduces the likelihood that investor assets will be commingled with the platform's business liabilities."
      },
      {
        question: "What are the main risks an investor should understand?",
        answer: "Real estate and private assets involve market risk, the risk of reduced rental income, vacancy, tenant default, higher repair costs, legal disputes, delayed exit, changes in interest rates, and changes in regulatory requirements. Flip and development strategies involve greater timing risk, contractor execution risk, and sale price risk. In addition, secondary liquidity is generally limited, and investors should be prepared to hold their position for longer than originally anticipated."
      },
      {
        question: "Can an investment be used to obtain a Golden Visa?",
        answer: "Only if the specific property and the then-current rules of the UAE immigration authorities allow for this. Requirements relating to the investment amount, asset type, holding period, property status, and document package may change, and any such decision must therefore be assessed against the latest government rules and the platform's official documentation."
      }
    ]
  },
  {
    title: "11. Additional Programs and Special Products",
    items: [
      {
        question: "Does RWA.capital have a referral or loyalty program?",
        answer: "Yes. The referral program documentation is available in the relevant section of the website."
      },
      {
        question: "How do promo codes work?",
        answer: "A promo code is typically applied at the time of registration by a new client."
      },
      {
        question: "What is a flip investment, and how does it work?",
        answer: "A flip investment is a transaction with a short value-creation cycle, where the core objective is to acquire an asset, increase its value through renovation, repositioning, improved occupancy, or another value-add strategy, and then sell it. The investor's main return is generated not through regular dividends, but through profit realized on exit. The timeline, stages, works budget, and sales strategy are disclosed to registered investors prior to investment."
      },
      {
        question: "How can I track the progress of a flip or value-add transaction?",
        answer: "We regularly publish milestone updates for investors in the relevant project, including the status of the works, budget versus plan, photographs, timelines, key events, property marketing progress, and the expected sale timing."
      }
    ]
  }
]);
</script>

<style scoped lang="scss">
.faq-page {
  background-color: var(--main-color);
  padding-bottom: 40px;
  min-height: 100vh;
}

.tabs-wrap {
  display: flex;
  justify-content: center;
  padding: 24px 16px 16px;
}

.content-wrap {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 16px;
}

.faq-section {
  margin-bottom: 24px;
}

.section-title {
  padding: 16px 0 8px;
  font-weight: 600;
}

.question {
  font-weight: 500;
}

.answer {
  line-height: 1.6;
  margin: 0;
}

.table-wrap {
  max-width: 100%;
  margin: 16px 0;
}
</style>
